import Backbone from 'backbone'
import template from './group-form.hbs'
import AdvancedPermissionsForm, {
	setPermissionsForGroupType
} from './advanced-permissions-form'
import OrgPermissionsForm, {getViewOrgPermissions} from './org-permissions-form'
import appDisplay from 'scoreboard/components/app-display'
import Members from '../members'
import _ from 'lodash'
import i18n from 'scoreboard/components/i18n'
import formHelper from 'scoreboard/components/form-helper'
import select from 'scoreboard-select'
import MarkedField from 'scoreboard/components/marked-field'
import requiredValidator from 'scoreboard/components/validators/required-validator'
import permissions from 'scoreboard/components/permissions'
import license from 'scoreboard/components/license'
import user from 'scoreboard/components/user'
import constants from 'scoreboard/components/constants'
import GroupDatasets from './group-datasets'
import projects from 'scoreboard/projects/projects'
import BusyButton from 'scoreboard/components/busy-button'
import groupType from './group-type'

function hasNoViewableOrgPermissions(groupModel) {
	return !user.isBuiltInAdmin() && (_.isEmpty(groupModel.get('viewableOrgIds'))
		|| !permissions.anyModifyViewOrgPermissions())
}

const LocalAdminsView = Members.extend({

	addMessage: function() {
		return i18n.get('admin.group.groupLocalAdmins.add')
	}
})

/**
 * @emits 'change' when a principal (member/admin) is added or removed, or
 * associated permissions (org, dataset, etc) are updated.
 * To bind all changes to a formView:
 * groupForm.on('change', this.formView.change.bind(this.formView))
 */
var GroupForm = Backbone.View.extend({

	/** @type {Group} */
	model: null,

	className: 'panel group-form right-panel',

	events: {
		'click .advanced-permissions': 'advancedPermissions',
		'click .org-permissions': 'orgPermissions',
		'click .datasets': 'datasets',
		'change [name=type]': 'setDefaultPermissionIds'
	},

	/**
	 * @param options will be passed through to GroupDatasets
	 * @param {FormViewOptions} options.formViewOptions
	 */
	initialize: function(options = {}) {
		this.options = options
		if (this.model.isNew()) {
			this.model.set('type', groupType.defaultMessageKey())
			// ticket:18831 #13008 current user should be a group admin
			if (user.model.id != constants.GROUP_ADMIN_ID) {
				var u = _.extend({ type: 'user' }, user.model.attributes)
				this.model.set('admins', [ u ])
			}
		}
		this.treeItemsById = _.keyBy(this.model.get('treeItems'), 'id')
		this.permissionsChosen = !this.model.isNew()
		//note since the form view is coming from the parent, let the parent
		//handle it's destruction
		this.formView = options.formView
	},

	setDefaultPermissionIds: function() {
		// #15038 changing group type should change the permissions for the
		// group as long as permissions weren't previously manually chosen.
		// This should happen regardless of whether it's a new or existing
		// group
		if (this.permissionsChosen) {
			return
		}

		// fill in default permissions (#7298 #13148)
		delete this.model.attributes.permissionIds
		this.setPermissionsForGroupType()
	},

	setPermissionsForGroupType: function(group) {
		setPermissionsForGroupType(
			this.model, this.$('[name=type]').val(), group)
	},

	render: function() {
		this.prepLocalAdmins()
		this.prepMembers()

		var context = _.defaults({}, this.model.attributes, {
			label: i18n.get('admin.group.newGroup')
		})
		this.$el.empty().append(template(context))

		this.$('.group-type').append(groupType.renderSelect({
			name: 'type'
		}))

		this.$('.group-form-content').append(this.members.render().el,
			this.localAdmins.render().el)

		this.renderMemberImages(this.localAdmins)
		this.renderMemberImages(this.members)

		formHelper.writeToDom(this.model, this.el)

		this.labelField = new MarkedField({
			el: this.$('[name=label]'),
			validator: requiredValidator
		}).render()

		this.toggleDatasetsButton()

		select(this.el.querySelectorAll('select'))

		if (this.model.isNew()) {
			this.setDefaultPermissionIds()
		}

		this._orgPermissionButton = new BusyButton({el: this.$('.org-permissions')})
		let enable = !hasNoViewableOrgPermissions(this.model)
		this._orgPermissionButton.toggle(enable)

		return this
	},

	prepLocalAdmins: function() {
		this.localAdmins = new LocalAdminsView({
			exclude: this.model,
			title: 'admin.group.groupLocalAdmins.short',
			noneMessage: i18n.get('common.thereAreNo', 'admin.group.groupLocalAdmins.short'),
			editable: !!this.model.get('changeUsers'),
			members: this.model.get('admins'),
			users: true,
			url: 'api/principals',
			icon: 'user-avatar'
		})
		this.localAdmins.on('change', this.trigger.bind(this, 'change'))
	},

	prepMembers: function() {
		this.members = new Members({
			exclude: this.model,
			editable: !!this.model.get('changeUsers'),
			members: this.model.get('members'),
			users: true,
			url: 'api/principals',
			icon : function(member) { return member.type == 'user' ? 'user-avatar' : 'profile' }
		})
		this.members.on('change', this.trigger.bind(this, 'change'))
	},

	renderMemberImages: function(view) {
		// Assign user images for admin and members lists
		// TODO: Insert real image when it's included in the json
		// If it is not included, the default "user-avatar" icon will show

		/*
		 _.each(view.options.members, function(member) {
		 	view.$el.find('[data-member-id=' + member.id + '] .member-icon')
		 		.empty().append('<img class="avatar" src="/cms/resources/images3/scott.jpg">')
		 }.bind(this))
		 */
	},

	// shows/hides the datasets button as appropriate
	toggleDatasetsButton: function() {
		var isShowingDatasetsButton = license.datasets
		if (isShowingDatasetsButton) {
			this.$('.admin-buttons-group').addClass('is-showing-datasets-button')
		} else {
			// Remove button so the CSS can target the correct last-child
			this.$('.datasets').remove()
		}
	},

	advancedPermissions: function() {
		var form = new AdvancedPermissionsForm({
			model: this.model,
			type: this.$('[name=type]').val()
		})
		appDisplay.dialog(form)
		form.on('done', function(dirty) {
			this.permissionsChosen = true
			if (dirty) {
				this.trigger('change')
			}
		}.bind(this))
	},

	orgPermissions: function() {
		if (hasNoViewableOrgPermissions(this.model)) {
			this.formView.errorHtml(i18n.get('admin.viewOrganizationPermissions.noModifyPermission'))
			this.once('change', () => this.formView.clearErrors())
			return
		}

		this._orgPermissionButton.loading()
		let projectId = projects.selectedOrFirstId()
		let groupId = this.model.get('id')
		getViewOrgPermissions(projectId, groupId).then(data => {
			this._orgPermissionButton.enable()
			const orgPermissions = new OrgPermissionsForm({
				model: this.model,
				projectId,
				viewOrgPermissions: data
			})
			orgPermissions.on('done', (selections, treeItems, dirty) => dirty && this.trigger('change'))
			appDisplay.dialog(orgPermissions)
		})
	},

	datasets: function() {
		var form = new GroupDatasets(_.extend({
			model: this.model,
			treeItemsById: this.treeItemsById,
			groupType: this.$('[name=type]').val()
		}, this.options))
		appDisplay.dialog(form)
		form.on('done', () => {
			this.trigger('change')
			this.datasetPermissionsModified = true
		})
	},

	readFromDom: function() {
		var group = formHelper.readFromDom(this.el)
		group.id = this.model.id
		group.members = this.members.getMembers()
		group.admins = this.localAdmins.getMembers()
		return group
	},

	doneSaving: function(e) {
		this.$('.group-label').text(this.model.get('label'))
		this.$('.group-id').removeClass('is-not-displayed')
			.text(`(${this.model.id})`)
		// note additional behavior defined in admin-users, since that's where
		// the submit buttons and form tag live

		this._setCurrentGroupIdOnAllDatasetPrincipalsWithoutIds()
	},

	// #31978 If the newly-saved group is brand-new, our previous representation
	// of it will not have an ID populated on it, and dataset association logic
	// will fail. Since we only save groups one-at-a-time, we may safely assume
	// that the ID should be the one we just saved - i.e. this.model.id
	_setCurrentGroupIdOnAllDatasetPrincipalsWithoutIds: function() {
		let datasets = this.model.get('datasets')
		_(datasets)
			.flatMap(it => it.permissions)
			.filter(it => _.isNil(it.principal.id))
			// Type check added for explicitness. Type is always 'group' because
			// that is the only principle type we are creating in this component
			// and already-saved principals won't have null IDs
			.filter(it => it.principal.type === 'group')
			.forEach(permission => {
				permission.principal.id = this.model.id
			})
	},

	// #51214 not autoRemove removes more than it should, so we can't use it
	// here
	remove: function() {
		Backbone.View.prototype.remove.apply(this, arguments)
		_.invokeMap([this.members, this.localAdmins, this.labelField,
			this._orgPermissionButton], 'remove')
	}

})

export default GroupForm

GroupForm.__filename__ = 'admin/principals/groups/group-form.js'
